import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useAuthStatus from "../helper/useAuthStatus";
import { nonProtectedRoute } from "../helper/useAuthStatus";
import { Box, CircularProgress } from "@mui/material";
import ErrorPage from "./ErrorPage";

const ProtectedRoute = () => {
  const { loading, error } = useAuthStatus();
  const isAuthenticated = useSelector(
    (state) => state.userAuth.isAuthenticated
  );
  const isNewUser = useSelector((state) => state.userAuth.isNewUser);
  const pin = useSelector((state) => state.userAuth.pin);
  const pinVerified = useSelector((state) => state.userAuth.pinVerified);
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
          position: "fixed",
          top: 0,
          left: 0,
          backgroundColor: "background.default",
        }}
      >
        <CircularProgress color="brand" />
      </Box>
    );
  }

  // Handle error states
  if (error) {
    if (error === 'Token expired' || error === 'Unauthorized') {
      // Only redirect if not already on login page
      if (location.pathname !== '/login') {
        return <Navigate to="/login" replace state={{ from: location, error: error }} />;
      }
      return <Outlet />;
    }
    return <ErrorPage to="/error-test" replace state={{ error: error }} />;
  }

  // If user is authenticated and tries to access login page, redirect to /pin
  if (
    isAuthenticated &&
    (location.pathname === "/login" || location.pathname === "/signup") &&
    !isNewUser &&
    pin
  ) {
    if (pinVerified) {
      return <Navigate to="/transaction" replace />;
    } else {
      return <Navigate to="/pin" replace />;
    }
  }

  // If route is non-protected, allow access
  if (nonProtectedRoute.includes(location.pathname)) {
    return <Outlet />;
  }

  // If not authenticated and trying to access protected route, redirect to login
  if (!isAuthenticated) {
    // Only redirect if not already on login page
    if (location.pathname !== '/login') {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return <Outlet />;
  }

  if (!pinVerified && location.pathname !== "/pin" && !isNewUser && pin) {
    return <Navigate to="/pin" replace />;
  }

  if (isNewUser && location.pathname !== "/new") {
    return <Navigate to="/new" replace />;
  }

  if (!pin && location.pathname !== "/pin/set" && !isNewUser) {
    return <Navigate to="/pin/set" replace />;
  }

  // If authenticated and accessing protected route, allow access
  return <Outlet />;
};

export default ProtectedRoute;
